import { SUBSCRIPTION_ACTIONS } from './constants.js';

/**
 * Shared helper for subscription AJAX actions
 * @param {string} action - WordPress AJAX action name
 * @param {string} payment_method_id - Stripe payment method ID
 * @param {string} subscription_id - WordPress subscription ID
 * @param {string} loadingText - Text to display while processing
 * @param {string} nonce - Security nonce token
 * @returns {Promise<Object|null>} Response data or null on error
 */
async function subscriptionAction(action, payment_method_id, subscription_id, loadingText, nonce) {
	const formData = new FormData();
	formData.append('action', action);
	formData.append('payment_method_id', payment_method_id);
	formData.append('subscription_id', subscription_id);
	formData.append('security', nonce); // Add nonce for security.

	const disableButtons = document.querySelectorAll('.disable-when-action');

	disableButtons.forEach((button) => {
		button.disabled = true;
		button.innerHTML = loadingText;
		button.classList.remove('btn-primary');
		button.classList.add('bg-gray-200');
	});

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData,
		}).then((response) => response.json());

		if (!response.success) {
			throw new Error(response.data?.message || 'Unknown error');
		}
		return response.data;
	} catch (error) {
		console.error(`${action} failed:`, error.message);
		return { error: error.message };
	} finally {
		// Re-enable action buttons regardless of success/failure
		disableButtons.forEach((button) => {
			button.disabled = false;
			button.classList.add('btn-primary');
			button.classList.remove('bg-gray-200');
		});
	}
}

/**
 * Upgrade user's subscription
 * @param {string} payment_method_id - Stripe payment method ID
 * @param {string} subscription_id - WordPress subscription ID
 * @param {string} nonce - Security nonce token
 * @returns {Promise<Object|null>} Response data or null on error
 */
export async function upgradeSubscription(payment_method_id, subscription_id, nonce) {
	return subscriptionAction(SUBSCRIPTION_ACTIONS.UPGRADE, payment_method_id, subscription_id, 'Upgrading...', nonce);
}

/**
 * Downgrade user's subscription
 * @param {string} payment_method_id - Stripe payment method ID
 * @param {string} subscription_id - WordPress subscription ID
 * @param {string} nonce - Security nonce token
 * @returns {Promise<Object|null>} Response data or null on error
 */
export async function downgradeSubscription(payment_method_id, subscription_id, nonce) {
	return subscriptionAction(SUBSCRIPTION_ACTIONS.DOWNGRADE, payment_method_id, subscription_id, 'Downgrading...', nonce);
}

/**
 * Create new subscription for user
 * @param {string} payment_method_id - Stripe payment method ID
 * @param {string} subscription_id - WordPress subscription ID
 * @param {string} nonce - Security nonce token
 * @returns {Promise<Object|null>} Response data or null on error
 */
export async function createSubscription(payment_method_id, subscription_id, nonce) {
	return subscriptionAction(SUBSCRIPTION_ACTIONS.CREATE, payment_method_id, subscription_id, 'Creating...', nonce);
}

/**
 * Create subscription after 3DS confirmation (skips card verification)
 */
export async function createSubscriptionAfter3DS(payment_method_id, subscription_id, nonce) {
	const formData = new FormData();
	formData.append('action', SUBSCRIPTION_ACTIONS.CREATE);
	formData.append('payment_method_id', payment_method_id);
	formData.append('subscription_id', subscription_id);
	formData.append('security', nonce);
	formData.append('skip_3ds_verify', '1');

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData,
		}).then((r) => r.json());

		if (!response.success) {
			throw new Error(response.data?.message || 'Unknown error');
		}
		return response.data;
	} catch (error) {
		console.error('createSubscriptionAfter3DS failed:', error.message);
		return { error: error.message };
	}
}

/**
 * Cancel user's subscription
 * @param {string} nonce - Security nonce token
 * @returns {Promise<Object|null>} Response data or null on error
 */
export async function cancelSubscription(nonce) {
	const formData = new FormData();
	formData.append('action', 'wpcore_cancel_subscription');
	formData.append('security', nonce);

	const disableButtons = document.querySelectorAll('.disable-when-action');

	disableButtons.forEach((button) => {
		button.disabled = true;
		button.innerHTML = wpcore.direct_t({ en: 'Canceling...', fr: 'Annulation...' });
		button.classList.remove('btn-primary');
		button.classList.add('bg-gray-200');
	});

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData,
		}).then((response) => response.json());

		if (!response.success) {
			throw new Error(response.data?.message || 'Unknown error');
		}
		return response.data;
	} catch (error) {
		console.error('Cancel subscription failed:', error.message);
		return { error: error.message };
	}
}

/**
 * Set default payment method for customer
 * @param {string} paymentMethodId - Stripe payment method ID
 * @returns {Promise<Object|null>} Response data or null on error
 */
export async function setDefaultPaymentMethod(paymentMethodId) {
	const formData = new FormData();
	formData.append('action', 'wpcore_set_default_payment_method');
	formData.append('nonce', wpcore.nonce);
	formData.append('payment_method_id', paymentMethodId);

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData,
		}).then((response) => response.json());

		if (!response.success) {
			throw new Error(response.data?.message || 'Unknown error');
		}
		return response.data;
	} catch (error) {
		console.error('Set default payment method failed:', error.message);
		return { error: error.message };
	}
}

/**
 * Create payment intent for one-time payment
 * @param {string} payment_method_id - Stripe payment method ID (optional for auto-charge)
 * @param {string} nonce - Security nonce token
 * @param {Object} metadata - Additional metadata (booking_id, date, time, etc.)
 * @returns {Promise<Object|null>} Response data with payment_intent_id, client_secret, status, or null on error
 */
export async function createPaymentIntent(payment_method_id = null, nonce, metadata = {}) {
	const formData = new FormData();
	formData.append('action', 'wpcore_create_payment_intent');
	formData.append('nonce', nonce);

	if (payment_method_id) {
		formData.append('payment_method_id', payment_method_id);
	}

	// Append metadata
	Object.keys(metadata).forEach((key) => {
		formData.append(`metadata[${key}]`, metadata[key]);
	});

	const disableButtons = document.querySelectorAll('.disable-when-action');

	disableButtons.forEach((button) => {
		button.disabled = true;
		button.innerHTML = wpcore.direct_t({ en: 'Processing payment...', fr: 'Traitement du paiement...' });
		button.classList.remove('btn-primary');
		button.classList.add('bg-gray-200');
	});

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData,
		}).then((response) => response.json());

		if (!response.success) {
			throw new Error(response.data?.message || 'Unknown error');
		}
		return response.data;
		// Returns: { payment_intent_id, client_secret, status, amount, currency }
	} catch (error) {
		console.error('Create payment intent failed:', error.message);
		return { error: error.message };
	}
}

/**
 * Finalize session booking after client-side PaymentIntent confirmation (e.g. 3DS).
 */
export async function finalizeSessionPayment(nonce, paymentIntentId) {
	const formData = new FormData();
	formData.append('action', 'wpcore_finalize_session_payment');
	formData.append('nonce', nonce);
	formData.append('payment_intent_id', paymentIntentId);

	try {
		const response = await fetch(wpcore.ajaxUrl, {
			method: 'POST',
			body: formData,
		}).then((r) => r.json());

		if (!response.success) {
			throw new Error(response.data?.message || 'Unknown error');
		}
		return response.data;
	} catch (error) {
		console.error('finalizeSessionPayment failed:', error.message);
		return { error: error.message };
	}
}
